import {
  AdminIcon,
  CustomerListIcon,
  DashboardIcon,
  DocumentsIcons,
  EventsIcon,
  ExpenseTrackerIcon,
  FormsIcon,
  GroupsIcon,
  HelpIcon,
  IntegrationsIcon,
  LeadManagementIcon,
  MembersIcon,
  OrgSettingsIcon,
  QuickCollectIcon,
  ReportsIcon,
  SubscriptionIcon,
  TeamManagementIcon,
  TransactionsIcon
} from '@images';

import {
  ADMIN,
  BRANCHES,
  DASHBOARD,
  DOCS,
  EVENTS,
  EXPENSE_TRACKER,
  FORMS,
  GROUPS,
  HELP,
  LEAD_MANAGEMENT,
  MEMBERS,
  ORG_DASHBOARD,
  ORG_SETTINGS,
  QUICK_COLLECT,
  REPORTS,
  SUBSCRIPTIONS,
  TALLY_INTEGRATIONS,
  TEAM_MANAGEMENT,
  TRANSACTIONS
} from '@constants/routes';
import { WebhooksEvents } from '@features/docs/constants';

import { FEATURE_FLAGS } from './feature';
import UserPermissionTypes from './permissions';

export const groupsTabPathPattern =
  /^\/groups(\/add|\/grp_[A-Za-z0-9]*(?:\/(?:edit|add-members))?)?$/;

const INDIVIDUAL_MENU_ITEMS = [
  {
    id: 'groups',
    Icon: GroupsIcon,
    label: 'Groups',
    route: GROUPS.HOME,
    permissions: [UserPermissionTypes.GROUP_VIEW]
  },
  {
    id: 'quick-collect',
    Icon: QuickCollectIcon,
    label: 'Quick Collect',
    route: QUICK_COLLECT.CREATE_LINK,
    permissions: [UserPermissionTypes.PAYMENT_ORDER_CREATE]
  },
  {
    id: 'transactions',
    Icon: TransactionsIcon,
    label: 'Transactions',
    route: TRANSACTIONS.TRANSACTIONS,
    permissions: [UserPermissionTypes.PAYMENT_ORDER_VIEW]
  },
  {
    id: 'event-registration',
    Icon: EventsIcon,
    label: 'Events',
    route: EVENTS.HOME,
    permissions: [UserPermissionTypes.EVENT_VIEW]
  },
  {
    id: 'forms',
    Icon: FormsIcon,
    label: 'Forms',
    route: FORMS.HOME,
    permissions: [UserPermissionTypes.FORM_VIEW],
    featureFlag: FEATURE_FLAGS.FORMS
  }
];

const PROFILE_ITEM_PATHS = [
  REPORTS,
  ORG_SETTINGS,
  ORG_DASHBOARD,
  EXPENSE_TRACKER.EXPENSE_DASHBOARD,
  EXPENSE_TRACKER.EXPENSE_LIST,
  EXPENSE_TRACKER.REPORTS,
  TEAM_MANAGEMENT.TEAM_MANAGEMENT,
  SUBSCRIPTIONS.PLAN_DETAILS,
  DOCS.OVERVIEW,
  DOCS.API_REFERENCE,
  DOCS.API_KEYS,
  DOCS.WEBHOOKS,
  DOCS.WEBHOOK_REFERENCE,
  HELP,
  LEAD_MANAGEMENT.HOME,
  TALLY_INTEGRATIONS.TALLY_INTEGRATIONS
];

const INDIVIDUAL_PROFILE_ITEMS = [
  [
    {
      id: 'report',
      Icon: ReportsIcon,
      label: 'Reports',
      route: REPORTS,
      permissions: [UserPermissionTypes.PAYMENT_ORDER_GENERATE_GST_REPORT]
    }
  ],
  [
    {
      id: 'org-settings',
      Icon: OrgSettingsIcon,
      label: 'Configurations',
      route: ORG_SETTINGS,
      permissions: [
        UserPermissionTypes.SUBSCRIPTION_VIEW,
        UserPermissionTypes.INTEGRATION_EDIT
      ]
    },
    {
      id: 'subscriptions',
      Icon: SubscriptionIcon,
      label: 'Plan & Billing',
      route: SUBSCRIPTIONS.PLAN_DETAILS,
      permissions: [UserPermissionTypes.SUBSCRIPTION_VIEW]
    }
    // {
    //   id: 'help',
    //   Icon: HelpIcon,
    //   label: 'Help',
    //   route: HELP
    // }
  ],
  [
    {
      id: 'docs',
      Icon: DocumentsIcons,
      label: 'Docs',
      route: DOCS.OVERVIEW,
      Permissions: [UserPermissionTypes.INTEGRATION_EDIT],
      children: [
        { id: 'overview', label: 'Overview', route: DOCS.OVERVIEW },

        {
          id: 'configuration',
          label: 'Configuration',
          route: DOCS.API_KEYS,
          children: [
            {
              id: 'api-keys',
              label: 'API Keys',
              route: DOCS.API_KEYS
            },
            {
              id: 'webhooks',
              label: 'Webhooks',
              route: DOCS.WEBHOOKS
            }
          ]
        },
        {
          id: 'api-reference',
          label: 'API Reference',
          route: DOCS.API_REFERENCE
        },
        {
          id: 'webhook-reference',
          label: 'Webhook Reference',
          route: DOCS.WEBHOOK_PAYMENT,
          children: WebhooksEvents.map((event) => ({
            id: event.id,
            label: event.label,
            route: event.route
          }))
        }
      ]
    }
  ]
];

// Here
const ORG_PROFILE_SECTIONS = [
  // Section 1
  [
    {
      id: 'org-dashboard',
      Icon: DashboardIcon,
      label: 'Organization Dashboard',
      route: ORG_DASHBOARD,
      permissions: [UserPermissionTypes.DASHBOARD_VIEW],
      icon: 'dashboard'
    },
    {
      id: 'report',
      Icon: ReportsIcon,
      label: 'Reports',
      route: REPORTS,
      permissions: [UserPermissionTypes.PAYMENT_ORDER_GENERATE_GST_REPORT]
    },
    {
      id: 'expence-tracker',
      Icon: ExpenseTrackerIcon,
      label: 'Expense Tracker',
      route: EXPENSE_TRACKER.EXPENSE_DASHBOARD,
      permissions: [UserPermissionTypes.EXPENSE_VIEW],
      featureFlag: FEATURE_FLAGS.EXPENSE_TRACKER,
      children: [
        {
          id: 'expense-tracker-dashboard',
          label: 'Expense Dashboard',
          route: EXPENSE_TRACKER.EXPENSE_DASHBOARD
        },
        {
          id: 'expense-tracker-list',
          label: 'Expense List',
          route: EXPENSE_TRACKER.EXPENSE_LIST
        },
        {
          id: 'expense-tracker-reports',
          label: 'Reports',
          route: EXPENSE_TRACKER.REPORTS
        }
      ]
    },
    {
      id: 'lead-management',
      Icon: LeadManagementIcon,
      label: 'Lead Management',
      route: LEAD_MANAGEMENT.LEAD_DASHBOARD,
      permissions: [UserPermissionTypes.LEAD_MANAGEMENT_CREATE],
      featureFlag: FEATURE_FLAGS.LEAD_MANAGEMENT,
      children: [
        {
          id: 'lead-management-dashboard',
          label: 'Dashboard',
          route: LEAD_MANAGEMENT.LEAD_DASHBOARD
        },
        {
          id: 'lead-management-list',
          label: 'Leads',
          route: LEAD_MANAGEMENT.LEAD_LIST,
          additionalChildRoutes: [
            LEAD_MANAGEMENT.LEAD_ADD,
            LEAD_MANAGEMENT.LEAD_DETAILS,
            LEAD_MANAGEMENT.LEAD_EDIT
          ]
        },
        {
          id: 'lead-management-activities',
          label: 'My Activities',
          route: LEAD_MANAGEMENT.LEAD_ACTIVITIES
        },
        {
          id: 'lead-management-reports',
          label: 'Reports',
          route: LEAD_MANAGEMENT.LEAD_REPORTS,
          permissions: [UserPermissionTypes.LEAD_REPORT_GENERATE]
        },
        {
          id: 'lead-management-settings',
          label: 'Settings',
          route: LEAD_MANAGEMENT.LEAD_SETTINGS
        }
      ]
    },
    {
      id: 'tally-integration',
      Icon: IntegrationsIcon,
      label: 'Tally Integration',
      route: TALLY_INTEGRATIONS.TALLY_INTEGRATIONS,
      permissions: [UserPermissionTypes.INTEGRATION_EDIT],
      featureFlag: FEATURE_FLAGS.TALLY_INTEGRATION
    }
  ],
  // Section 2
  [
    {
      id: 'org-settings',
      Icon: OrgSettingsIcon,
      label: 'Configurations',
      route: ORG_SETTINGS,
      permissions: [
        UserPermissionTypes.SUBSCRIPTION_VIEW,
        UserPermissionTypes.INTEGRATION_EDIT
      ]
    },
    {
      id: 'team-management',
      Icon: TeamManagementIcon,
      label: 'Team Management',
      route: TEAM_MANAGEMENT.TEAM_MANAGEMENT,
      permissions: [
        UserPermissionTypes.ORG_ADMIN_VIEW,
        UserPermissionTypes.OWNER_VIEW,
        UserPermissionTypes.BRANCH_ADMIN_VIEW,
        UserPermissionTypes.GROUP_ADMIN_VIEW,
        UserPermissionTypes.SALES_MANAGER_CREATE
      ]
    },
    {
      id: 'subscriptions',
      Icon: SubscriptionIcon,
      label: 'Plan & Billing',
      route: SUBSCRIPTIONS.PLAN_DETAILS,
      permissions: [UserPermissionTypes.SUBSCRIPTION_VIEW]
    }
    // {
    //   id: 'help',
    //   Icon: HelpIcon,
    //   label: 'Help',
    //   route: HELP
    // }
  ],
  // Section 3
  [
    {
      id: 'docs',
      label: 'Docs',
      route: DOCS.OVERVIEW,
      Icon: DocumentsIcons,
      Permissions: [UserPermissionTypes.INTEGRATION_EDIT],
      children: [
        { id: 'overview', label: 'Overview', route: DOCS.OVERVIEW },
        {
          id: 'configuration',
          label: 'Configuration',
          route: DOCS.API_KEYS,
          children: [
            {
              id: 'api-keys',
              label: 'API Keys',
              route: DOCS.API_KEYS
            },
            {
              id: 'webhooks',
              label: 'Webhooks',
              route: DOCS.WEBHOOKS
            }
          ]
        },
        {
          id: 'api-reference',
          label: 'API Reference',
          route: DOCS.API_REFERENCE
        },
        {
          id: 'webhook-reference',
          label: 'Webhook Reference',
          route: DOCS.WEBHOOK_PAYMENT,
          children: WebhooksEvents.map((event) => ({
            id: event.id,
            label: event.label,
            route: event.route
          }))
        }
      ]
    }
  ]
];

const ORG_MENU_ITEMS = [
  {
    id: 'dashboard',
    Icon: DashboardIcon,
    label: 'Dashboard',
    route: DASHBOARD,
    permissions: [UserPermissionTypes.DASHBOARD_VIEW]
  },
  {
    id: 'members',
    Icon: MembersIcon,
    label: 'Members',
    route: MEMBERS.MEMBERS,
    permissions: [UserPermissionTypes.BRANCH_MEMBER_VIEW]
  },
  {
    id: 'groups',
    Icon: GroupsIcon,
    label: 'Groups',
    route: GROUPS.HOME,
    permissions: [UserPermissionTypes.GROUP_VIEW]
  },
  {
    id: 'quick-collect',
    Icon: QuickCollectIcon,
    label: 'Quick Collect',
    route: QUICK_COLLECT.CREATE_LINK,
    permissions: [UserPermissionTypes.PAYMENT_ORDER_CREATE]
  },
  {
    id: 'transactions',
    Icon: TransactionsIcon,
    label: 'Transactions',
    route: TRANSACTIONS.TRANSACTIONS,
    permissions: [UserPermissionTypes.PAYMENT_ORDER_VIEW]
  },
  {
    id: 'event-registration',
    Icon: EventsIcon,
    label: 'Events',
    route: EVENTS.HOME,
    permissions: [
      UserPermissionTypes.EVENT_VIEW,
      UserPermissionTypes.EVENT_VOLUNTEER_VIEW
    ]
  },
  {
    id: 'forms',
    Icon: FormsIcon,
    label: 'Forms',
    route: FORMS.HOME,
    permissions: [UserPermissionTypes.FORM_VIEW],
    featureFlag: FEATURE_FLAGS.FORMS
  },
  {
    id: 'branches',
    Icon: OrgSettingsIcon,
    label: 'Branch Settings',
    route: BRANCHES.EDIT_BRANCH,
    permissions: [UserPermissionTypes.BRANCH_EDIT]
  }
];

const ADMIN_MENU_ITEMS = [
  {
    id: 'kyc-documents',
    Icon: AdminIcon,
    label: 'KYC Documents',
    route: ADMIN.KYC_DOCUMENTS
  },
  {
    id: 'customers-list',
    Icon: CustomerListIcon,
    label: 'Customers List',
    route: ADMIN.CUSTOMERS_LIST,
    permissions: [UserPermissionTypes.CUSTOMER_LIST_VIEW]
  }
];

const HELP_ITEM = {
  id: 'help',
  Icon: HelpIcon,
  label: 'Help',
  route: HELP
};

export {
  ADMIN_MENU_ITEMS,
  HELP_ITEM,
  INDIVIDUAL_MENU_ITEMS,
  INDIVIDUAL_PROFILE_ITEMS,
  ORG_MENU_ITEMS,
  ORG_PROFILE_SECTIONS,
  PROFILE_ITEM_PATHS
};
